<template>
  <div>
    <v-card id="invoice-list">
      <v-card-text class="d-flex align-center flex-wrap pb-0">
        <div class="d-flex align-center pb-5">
          <!-- create invoice -->
          <v-btn
            v-if="can('write', 'Invoice')"
            color="primary"
            class="me-3 invoice-button"
            :disabled="outstandingCount >= 5"
            :to="{ name: 'apps-invoice-add' }"
          >
            <v-icon
              size="18"
              class="me-1"
            >
              {{ icons.mdiPlus }}
            </v-icon>
            <span>{{ t('Create Invoice') }}</span>
          </v-btn>
        </div>

        <div
          v-if="outstandingCount >= 5"
          class="create-invoice-hint"
        >
          <small>{{ t('invoice-create-limit') }}</small>
        </div>

        <v-spacer v-if="$vuetify.breakpoint.smAndUp"></v-spacer>

        <div class="row text-right pb-sm-6 pb-5">
          <div class="col-sm-4 pa-2 offset-sm-1 col-6">
            <v-autocomplete
              v-model="toggleSelected"
              :items="branchOptions"
              item-text="name"
              item-value="value"
              single-line
              outlined
              dense
              hide-details
              clearable
              :placeholder="t('Select Branch Status')"
              @change="activeToggleChange()"
            ></v-autocomplete>
          </div>

          <div class="col-sm-4 col-6 pa-2">
            <!-- filter branch -->
            <v-combobox
              v-model="branchFilters"
              :items="branches"
              item-text="name"
              single-line
              multiple
              outlined
              dense
              hide-details
              clearable
              :placeholder="t('Select Branch')"
              :class="{ 'invoice-list-branch': $vuetify.breakpoint.smAndUp }"
            ></v-combobox>
          </div>

          <div class="col-sm-3 col-6 pa-2">
            <!-- filter status -->
            <v-select
              v-model="statusFilter"
              :items="statusOptions"
              single-line
              outlined
              dense
              hide-details
              clearable
              :placeholder="t('Select Status')"
              :class="{ 'invoice-list-status': $vuetify.breakpoint.smAndUp }"
            ></v-select>
          </div>
        </div>
      </v-card-text>

      <v-card-text class="row pb-0 pr-0">
        <div class="col-sm-2 col-12 pb-sm-8 pb-1 pl-2 pr-1">
          <!-- Date range -->
          <v-menu
            v-model="isDateMenuOpen"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :append-icon="icons.mdiCalendar"
                :value="dateRangeText"
                readonly
                dense
                outlined
                hide-details
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="dateRange"
              color="primary"
              :first-day-of-week="1"
              range
            >
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                block
                @click="dateRangeChange()"
              >
                Set dates
              </v-btn>
            </v-date-picker>
          </v-menu>
        </div>

        <v-spacer></v-spacer>

        <div class="d-flex align-center col-sm-4 col-12 pb-sm-8 pb-1 pr-1">
          <!-- search invoice -->
          <v-tooltip
            :left="$vuetify.breakpoint.smAndUp"
            :top="$vuetify.breakpoint.xsOnly"
            :nudge-bottom="$vuetify.breakpoint.smAndUp ? 20 : 0"
            :nudge-top="$vuetify.breakpoint.xsOnly ? 5 : 0"
            :nudge-right="$vuetify.breakpoint.xsOnly ? 100 : 0"
            max-width="250"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-avatar
                size="30"
                class="mr-3 hover-pointer"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon size="25">
                  {{ icons.mdiAlertCircleOutline }}
                </v-icon>
              </v-avatar>
            </template>
            <h3 class="my-2">
              {{ t('How to use the search bar') }}
            </h3>
            <p class="mb-1">
              {{ t('You can search using') }}:
            </p>
            <ul class="mb-2">
              <li>
                {{ t('#Invoice No.') }}
              </li>
              <li>
                {{ t('Car Registration No.') }}
              </li>
              <li>
                {{ t('Customer Name') }}
              </li>
            </ul>
          </v-tooltip>
          <v-text-field
            v-model="searchText"
            :append-icon="icons.mdiMagnify"
            single-line
            dense
            outlined
            hide-details
            :placeholder="t('Search Invoice')"
            :class="{ 'invoice-list-search': $vuetify.breakpoint.mdAndUp }"
          ></v-text-field>
        </div>
        <div class="d-flex align-center col-sm-2 col-12 pl-3 pb-sm-8 pb-6 pl-0 pr-1">
          <v-btn
            block
            color="primary"
            @click="searchInvoices"
          >
            <v-icon
              class="pr-2"
              size="30"
            >
              {{ icons.mdiMagnify }}
            </v-icon>
            Search
          </v-btn>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-text class="pt-5">
        <v-row>
          <v-col
            lg="2"
            cols="3"
          >
            <div class="d-flex align-center">
              <v-select
                v-model="tablePagination.per"
                :items="listLengthOptions"
                single-line
                outlined
                dense
                hide-details
                class="invoice-list-row-selection"
                @input="listLengthChange()"
              ></v-select>
              <div class="pl-3">
                {{ t('Rows') }}
              </div>
            </div>
          </v-col>

          <v-col
            lg="10"
            cols="9"
            class="d-flex justify-end"
          >
            <v-pagination
              v-model="tablePagination.current_page"
              total-visible="6"
              :length="tablePagination.total_page"
              @input="pageChange()"
            ></v-pagination>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- table -->
      <v-data-table
        :headers="tableColumns"
        :items="invoices"
        :options.sync="options"
        :items-per-page="100"
        :loading="loading"
        mobile-breakpoint="0"
        hide-default-footer
        :disable-sort="true"
        class="text-no-wrap"
      >
        <!-- Actions -->
        <template #[`item.actions`]="{item}">
          <div class="d-flex align-center justify-center">
            <!-- Edit -->
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  :to="{
                    name: (can('write', 'ReportActions') ? 'apps-invoice-edit' : 'apps-invoice-req'),
                    params: { id: item.id }
                  }"
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon size="18">
                    {{ icons.mdiPencilOutline }}
                  </v-icon>
                </v-btn>
              </template>
              <span>Edit</span>
            </v-tooltip>

            <!-- Void -->
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  v-if="item.settled_at === 'NULL'"
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="voidableInvoice = item; isVoidDialogVisible = true"
                >
                  <v-icon size="18">
                    {{ icons.mdiDeleteOutline }}
                  </v-icon>
                </v-btn>
              </template>
              <span>Void</span>
            </v-tooltip>
          </div>
        </template>

        <!-- SPD Reference Number -->
        <template #[`item.spd_number`]="{item}">
          <div
            v-if="(item.insurance_category.id == 1 || item.insurance_category.id == 2) && item.insurance_product == 'Motor insurance'"
            class="d-flex align-center justify-center"
          >
            <v-text-field
              v-model="item.spd_number"
              type="number"
              single-line
              dense
              outlined
              hide-details="auto"
              class="invoice-spd my-3"
              @mousewheel.native.prevent
            ></v-text-field>
            <v-btn
              icon
              small
              class="ml-2"
              :disabled="item.spd_number.length !== 4 && item.spd_number.length !== 0"
              @click="updateSpdNumber(item.id, item.spd_number)"
            >
              <v-icon size="18">
                {{ icons.mdiContentSave }}
              </v-icon>
            </v-btn>
          </div>
          <div v-else>
            NULL
          </div>
        </template>

        <!-- Invoice Number -->
        <template #[`item.invoice_number`]="{item}">
          <router-link
            class="font-weight-medium text-decoration-none"
            :to="{ name: 'apps-invoice-preview', params: { id: item.id } }"
          >
            {{ item.invoice_number }}
          </router-link>
        </template>

        <!-- Status -->
        <template #[`item.status`]="{item}">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-avatar
                size="30"
                :color="item.settled_at === 'NULL' ? 'error' : 'success'"
                :class="`v-avatar-light-bg ${item.settled_at === 'NULL' ? 'error' : 'success'}--text`"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon
                  size="18"
                  :color="item.settled_at === 'NULL' ? 'error' : 'success'"
                >
                  {{ item.settled_at === 'NULL' ? icons.mdiAlertCircleOutline : icons.mdiCheckCircleOutline }}
                </v-icon>
              </v-avatar>
            </template>
            <span>{{ item.settled_at === 'NULL' ? t('Outstanding') : t('Paid') }}</span>
          </v-tooltip>
        </template>
      </v-data-table>

      <!-- Void dialog -->
      <v-dialog
        v-model="isVoidDialogVisible"
        width="500"
      >
        <v-card>
          <v-card-title class="d-flex align-center mv-4">
            <div
              v-if="!can('write', 'ReportActions')"
              class="pr-1"
            >
              Request
            </div>
            Void {{ voidableInvoice.invoice_number }}?
            <v-spacer></v-spacer>
            <v-btn
              icon
              small
              @click="isVoidDialogVisible = false"
            >
              <v-icon size="22">
                {{ icons.mdiClose }}
              </v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text v-if="can('write', 'ReportActions')">
            This invoice will be voided. It won't be taken into any calculations.
          </v-card-text>

          <v-card-text v-else>
            <v-textarea
              v-model="newComment"
              dense
              outlined
              hide-details="auto"
              placeholder="Leave your comment here (required)"
              :disabled="loading"
              class="mb-4"
            ></v-textarea>
            This action will create a void request and this invoice will be voided after approved by auditor.
          </v-card-text>

          <v-card-actions>
            <v-btn
              v-if="can('write', 'ReportActions')"
              color="error"
              block
              :loading="voidLoading"
              :disabled="voidLoading"
              class="mt-3"
              @click="voidInvoice()"
            >
              Yes, remove
            </v-btn>

            <v-btn
              v-else
              color="error"
              block
              :loading="voidLoading"
              :disabled="voidLoading || (!newComment || newComment.trim() === '')"
              class="mt-3"
              @click="createRequest()"
            >
              Yes, send request
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-card-text class="pt-5">
        <v-row>
          <v-col
            lg="2"
            cols="3"
          >
            <div class="d-flex align-center">
              <v-select
                v-model="tablePagination.per"
                :items="listLengthOptions"
                single-line
                outlined
                dense
                hide-details
                class="invoice-list-row-selection"
                @input="listLengthChange()"
              ></v-select>
              <div class="pl-3">
                {{ t('Rows') }}
              </div>
            </div>
          </v-col>

          <v-col
            lg="10"
            cols="9"
            class="d-flex justify-end"
          >
            <v-pagination
              v-model="tablePagination.current_page"
              total-visible="6"
              :length="tablePagination.total_page"
              @input="pageChange()"
            ></v-pagination>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <div class="text-right">
      <v-btn
        :block="$vuetify.breakpoint.xsOnly"
        :loading="loadingPrint"
        :disabled="loadingPrint"
        color="primary"
        class="invoice-button mt-5"
        :class="{ 'loading-color': loadingPrint && !$vuetify.theme.dark }"
        @click="printReport()"
      >
        <v-icon
          v-if="!loadingPrint"
          size="18"
          class="me-2"
        >
          {{ icons.mdiPrinter }}
        </v-icon>
        <span>{{ t('Print Report') }}</span>
      </v-btn>
    </div>

    <v-card
      v-if="can('read', 'ReportSummaries')"
      class="my-5"
    >
      <v-card-title class="align-start">
        <span :class="{ 'text-sm': $vuetify.breakpoint.xsOnly }">{{ t('Summary for') }} {{ dateRangeText }}</span>
      </v-card-title>

      <v-card-text>
        <v-row class="py-3">
          <v-col
            cols="12"
            sm="6"
            lg="3"
            class="px-5 py-0"
            :class="{
              'dark-divider': $vuetify.theme.dark && $vuetify.breakpoint.mdAndUp,
              'light-divider': !$vuetify.theme.dark && $vuetify.breakpoint.mdAndUp
            }"
          >
            <Breakdowns
              field="After Discount"
              :summary="invoicesSummary.after_discount_summary"
              img="wallet"
              color="info"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            lg="3"
            class="px-5 py-0"
            :class="{
              'dark-divider': $vuetify.theme.dark && $vuetify.breakpoint.mdAndUp,
              'light-divider': !$vuetify.theme.dark && $vuetify.breakpoint.mdAndUp
            }"
          >
            <Breakdowns
              field="Insurance Premium"
              :summary="invoicesSummary.insurance_premium_summary"
              img="credit-card"
              color="success"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            lg="3"
            class="px-5 py-0"
            :class="{
              'dark-divider': $vuetify.theme.dark && $vuetify.breakpoint.mdAndUp,
              'light-divider': !$vuetify.theme.dark && $vuetify.breakpoint.mdAndUp
            }"
          >
            <Breakdowns
              field="Additional Fees"
              :summary="invoicesSummary.additional_fees_summary"
              img="atm-card"
              color="warning"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            lg="3"
            class="px-5 py-0"
          >
            <Breakdowns
              field="Road Tax"
              :summary="invoicesSummary.road_tax_summary"
              img="road-tax"
              color="accent"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import {
  mdiPlus,
  mdiCheckCircleOutline,
  mdiAlertCircleOutline,
  mdiDeleteOutline,
  mdiPencilOutline,
  mdiCalendar,
  mdiPrinter,
  mdiMagnify,
  mdiClose,
  mdiContentSave,
} from '@mdi/js'
import {
  onUnmounted,
  onMounted,
  ref,
  inject,
  computed,
} from '@vue/composition-api'
import { useRouter } from '@core/utils'
import { Breakdowns } from '@/components/apps/summaries'
import ability from '@/plugins/acl/ability'
import invoiceStoreModule from './invoiceStoreModule'

export default {
  components: {
    Breakdowns,
  },
  setup() {
    // Initializers
    const INVOICE_APP_STORE_MODULE_NAME = 'app-invoice'
    const store = inject('store')
    const snackbarService = inject('snackbarService')
    const t = inject('t')
    const can = inject('can')
    const { router } = useRouter()
    const isAuditor = ability.can('read', 'ReportSummaries')
    const format = isAuditor ? 'csv' : 'pdf'

    if (!store.hasModule(INVOICE_APP_STORE_MODULE_NAME)) {
      store.registerModule(INVOICE_APP_STORE_MODULE_NAME, invoiceStoreModule)
    }

    // Table Handlers
    const tableColumns = computed(() => (
      [
        { text: t('Actions'), value: 'actions', align: 'center' },
        { text: t('#Invoice No.'), value: 'invoice_number' },
        { text: t('Status'), value: 'status' },
        { text: t('Issue Date'), value: 'created_at' },
        { text: t('Due Date'), value: 'due_date' },
        { text: t('Customer Name'), value: 'customer_name' },
        { text: t('Contact No.'), value: 'customer_contact_number' },
        { text: t('Car Registration No.'), value: 'car_registration_number' },
        { text: t('Sum Insured'), align: 'right', value: 'sum_insured' },
        { text: t('Insurance Premium'), align: 'right', value: 'insurance_premium' },
        { text: t('After Discount'), align: 'right', value: 'after_discount' },
        { text: t('Road Tax'), align: 'right', value: 'road_tax' },
        { text: t('Additional Fees'), align: 'right', value: 'additional_fees' },
        { text: t('Total'), align: 'right', value: 'total' },
        { text: t('Party'), value: 'party' },
        { text: t('Branch'), value: 'branch' },
        { text: t('Insurance Product'), value: 'insurance_product' },
        { text: t('SPD Reference No.'), align: 'right', value: 'spd_number' },
        { text: t('Insurance'), value: 'insurance' },
        { text: t('Pickup Location'), value: 'pickup_location' },
        { text: t('Issuer'), value: 'issuer' },
        { text: t('General Remarks'), value: 'general_remarks' },
      ]
    ))

    // Properties
    const [dayNow, monthNow, yearNow] = new Date().toLocaleDateString('en-SG').split('/')
    const invoices = ref([])
    const invoicesSummary = ref({
      insurance_premium_summary: {},
      after_discount_summary: {},
      additional_fees_summary: {},
      road_tax_summary: {},
    })
    const tablePagination = ref({
      current_page: 1,
      per: 20,
      total_count: 0,
      total_page: 0,
    })
    const searchText = ref('')
    const options = ref({
      sortBy: ['id'],
      sortDesc: [false],
    })
    const loading = ref(false)
    const loadingPrint = ref(false)
    const userData = JSON.parse(localStorage.getItem('userData'))
    const newComment = ref(null)
    const statusFilter = ref('Outstanding')
    const toggleSelected = ref('active')
    const branchOptions = ref([
      { name: 'Ongoing branches', value: 'active' },
      { name: 'Closed branches', value: 'inactive' },
    ])
    const branchFilters = ref([])
    const dateRange = ref([`${yearNow}-${monthNow}-${dayNow}`])
    const statusOptions = ref(['Outstanding', 'Paid'])
    const listLengthOptions = ref([20, 50, 100])
    const isDateMenuOpen = ref(false)
    const isVoidDialogVisible = ref(false)
    const voidableInvoice = ref({})
    const voidLoading = ref(false)
    const outstandingCount = ref(null)

    // Utils
    const formatDate = date => {
      if (!date) return null
      const [year, month, day] = date.split('-')

      return `${day}/${month}/${year}`
    }

    // Computed
    const branches = computed(() => {
      switch (toggleSelected.value) {
        case 'active':
          return store.state.branches.filter(b => b.active)
        case 'inactive':
          return store.state.branches.filter(b => b.active === false)
        default:
          return store.state.branches
      }
    })
    const dateRangeText = computed(() => {
      if (Date.parse(dateRange.value[0]) > Date.parse(dateRange.value[1])) {
        const [start, end] = dateRange.value
        dateRange.value[0] = end
        dateRange.value[1] = start
      }

      const startDateText = formatDate(dateRange.value[0])
      const showEndDate = dateRange.value[0] !== dateRange.value[1] && formatDate(dateRange.value[1]) !== null
      const endDateText = showEndDate ? ` - ${formatDate(dateRange.value[1])}` : ''

      return `${startDateText}${endDateText}`
    })

    // Methods
    const isLoading = () => {
      loading.value = true
      snackbarService.info('Loading your list. Please hold...')
    }
    const doneLoading = () => {
      loading.value = false
      snackbarService.success('Done loading your list!')
    }
    const fetchInvoices = () => {
      store
        .dispatch('app-invoice/fetchInvoices', {
          page: tablePagination.value.current_page,
          per: tablePagination.value.per,
          search_text: searchText.value,
          branch_ids: branchFilters.value.map(branch => branch.id),
          start_date: dateRange.value[0],
          end_date: dateRange.value[1] ? dateRange.value[1] : dateRange.value[0],
          status: statusFilter.value,
          branch_toggle_filter: toggleSelected.value,
        })
        .then(response => {
          const { data, pagination, summary } = response.data
          invoices.value = data
          invoices.value.forEach(field => {
            Object.keys(field).forEach(key => {
              if (field[key] === null) field[key] = 'NULL'
            })
          })
          tablePagination.value = pagination
          invoicesSummary.value = summary
          outstandingCount.value = response.data.outstanding_count
          doneLoading()
        })
        .catch(error => {
          loading.value = false
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching invoices. Please refresh!')
        })
    }
    const pageChange = () => {
      isLoading()
      fetchInvoices()
    }
    const listLengthChange = () => {
      tablePagination.value.current_page = 1
      isLoading()
      fetchInvoices()
    }
    const dateRangeChange = () => {
      isDateMenuOpen.value = false
      tablePagination.value.current_page = 1
      isLoading()
      fetchInvoices()
    }
    const activeToggleChange = () => {
      branchFilters.value = []
    }
    const updateSpdNumber = (id, value) => {
      store
        .dispatch('app-invoice/updateInvoice', {
          id,
          invoice: { spd_number: value },
        })
        .then(response => snackbarService.success(response.data.message))
        .catch(error => snackbarService.error(error.response.data.message || 'Something went wrong while updating SPD Reference Number. Please refresh!'))
    }
    const printReport = () => {
      loadingPrint.value = true
      store
        .dispatch('app-invoice/printReport', {
          search_text: searchText.value,
          branch_ids: branchFilters.value.map(branch => branch.id),
          start_date: dateRange.value[0],
          end_date: dateRange.value[1] ? dateRange.value[1] : dateRange.value[0],
          status: statusFilter.value,
          branch_toggle_filter: toggleSelected.value,
          format,
        })
        .then(response => {
          if (response.type === 'application/json' || response.type === 'application/json; charset=utf-8') {
            response.text().then(res => {
              const error = JSON.parse(res)
              snackbarService.error(`${error.message} ${error.invoice_ids || ''}`, -1)
            })
          } else {
            const url = window.URL.createObjectURL(response)
            const a = document.createElement('a')
            a.href = url
            a.download = `invoice(${dateRangeText.value}).${format}`
            document.body.appendChild(a)
            a.click()
            a.remove()
          }
          loadingPrint.value = false
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while printing report. Please refresh!')
        })
    }
    const voidInvoice = () => {
      voidLoading.value = true
      store
        .dispatch('app-invoice/voidInvoice', { id: voidableInvoice.value.id })
        .then(response => {
          snackbarService.success(response.data.message)
          fetchInvoices()
          isVoidDialogVisible.value = false
          voidLoading.value = false
        })
        .catch(error => {
          voidLoading.value = false
          snackbarService.error(error.data.message || 'Something went wrong while voiding invoice. Please refresh!')
        })
    }
    const createRequest = () => {
      store
        .dispatch('requestStore/createRequest', {
          record_id: voidableInvoice.value.id,
          record_type: 'Invoice',
          requested_by_id: userData.id,
          action: 'Void',
          comment: newComment.value,
        })
        .then(response => {
          snackbarService.success(response.data.message)
          router.push('/apps/requests/list')
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while saving request. Please refresh!')
        })
    }
    const searchInvoices = () => {
      isLoading()
      fetchInvoices()
    }

    onMounted(() => searchInvoices())

    // Unmounted
    onUnmounted(() => {
      if (store.hasModule(INVOICE_APP_STORE_MODULE_NAME)) store.unregisterModule(INVOICE_APP_STORE_MODULE_NAME)
    })

    return {
      t,
      can,
      loading,
      loadingPrint,
      invoices,
      branches,
      outstandingCount,
      tablePagination,
      invoicesSummary,
      tableColumns,
      isDateMenuOpen,
      statusOptions,
      listLengthOptions,
      options,
      searchText,
      statusFilter,
      toggleSelected,
      branchOptions,
      branchFilters,
      dateRange,
      dateRangeText,
      fetchInvoices,
      pageChange,
      listLengthChange,
      dateRangeChange,
      activeToggleChange,
      formatDate,
      printReport,
      isVoidDialogVisible,
      voidableInvoice,
      voidLoading,
      voidInvoice,
      updateSpdNumber,
      createRequest,
      newComment,
      searchInvoices,

      icons: {
        mdiPlus,
        mdiCheckCircleOutline,
        mdiAlertCircleOutline,
        mdiDeleteOutline,
        mdiPencilOutline,
        mdiCalendar,
        mdiPrinter,
        mdiMagnify,
        mdiClose,
        mdiContentSave,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
  #invoice-list {
    .invoice-list-search {
      width: 29rem;
    }

    .invoice-list-status {
      max-width: 12.2rem;
    }

    .invoice-list-branch {
      max-width: 33rem;
    }

    .invoice-list-row-selection {
      max-width: 5.3rem;
    }

    .invoice-spd {
      min-width: 10rem;
    }

    @media #{map-get($display-breakpoints, 'xs-only')} {
      .invoice-spd {
        width: 100px;
      }
    }
  }

  .invoice-button {
    width: 251px;
  }

  .create-invoice-hint {
    max-width: 350px;
    padding-bottom: 22px;
  }

  ::v-deep .theme--dark.v-btn.v-btn--disabled.v-btn--has-bg.loading-color {
    background: #d3d3d3 !important;
    color: #eee !important;
  }

  .dark-divider {
    border-right: 1px solid rgba(231, 227, 252, 0.14) !important;
  }

  .light-divider {
    border-right: 1px solid rgba(94, 86, 105, 0.14);
  }
</style>
